import { threadTitle } from '../bailey/format';

/**
 * A conversation Bailey had with a client, read into what the front desk
 * shows: a title, what happened, the turns, and the moments between them the
 * client saw or heard (the free times, the booking offered, the link sent).
 *
 * What happened comes from the steps Bailey recorded and their results, never
 * from reading the words. When the server sends its own summaryTitle or
 * outcome (docs/FRONT_DESK_BACKEND.md), those win.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const isPhoneChannel = (channel) => /voice|phone|call/i.test(String(channel || ''));

function parse(text) {
    if (text && typeof text === 'object') return text;
    try { const v = JSON.parse(String(text || '')); return v && typeof v === 'object' ? v : null; } catch (e) { return null; }
}

/** "2026-09-19" as "Sat 19 Sept", read as a calendar day (no timezone shift). */
export function dayLabel(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "08:00" as "8:00am", "19:30" as "7:30pm". */
export function timeLabel(hhmm) {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
    if (!m) return '';
    const h = Number(m[1]);
    return `${(h % 12) || 12}:${m[2]}${h < 12 ? 'am' : 'pm'}`;
}

export function money(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '';
    return `£${Number.isInteger(v) ? v : v.toFixed(2)}`;
}

/** 262 as "4:22" for a row. */
export function clock(sec) {
    const s = Math.round(Number(sec) || 0);
    if (s <= 0) return '';
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** 262 as "4 min 22 s" for the top of a transcript. */
export function length(sec) {
    const s = Math.round(Number(sec) || 0);
    if (s <= 0) return '';
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (!m) return `${r} s`;
    return r ? `${m} min ${r} s` : `${m} min`;
}

const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

function list(items) {
    const a = items.filter(Boolean);
    if (a.length <= 1) return a[0] || '';
    return `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`;
}

/** "Haircut with Phoebe, Sat 19 Sept at 8:00am, £12" from an offer Bailey made. */
export function describeOffer(p, { withTime = true, withPrice = true } = {}) {
    if (!p) return '';
    const services = Array.isArray(p.services) ? p.services.filter(Boolean) : [];
    const what = services.length ? list(services) : 'An appointment';
    const who = firstName(p.stylist);
    const day = dayLabel(p.date);
    let out = who ? `${what} with ${who}` : what;
    if (day) out += `, ${day}`;
    if (withTime && p.slot) out += ` at ${timeLabel(p.slot)}`;
    if (withPrice && p.totalPrice != null && money(p.totalPrice)) out += `, ${money(p.totalPrice)}`;
    return out;
}

function slotsLine(slots) {
    const times = (slots || []).map(timeLabel).filter(Boolean);
    if (!times.length) return '';
    if (times.length <= 4) return list(times);
    return `${times.slice(0, 3).join(', ')} and ${times.length - 3} more`;
}

/**
 * The booking in a link Bailey sent (to finish on the booking page): the
 * stylist, the services, the day and the time, as the diary needs them.
 */
export function bookingFromLink(link) {
    const m = /\?(.*)$/.exec(String(link || ''));
    if (!m) return null;
    const q = new URLSearchParams(m[1]);
    const staffId = String(q.get('hairdresser') || '').trim();
    const serviceIds = String(q.get('services') || '').split(',').map((x) => x.trim()).filter(Boolean);
    const date = String(q.get('date') || '');
    const slot = String(q.get('slot') || '');
    if (!staffId || !serviceIds.length || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(slot)) return null;
    return { staffId, serviceIds, date, slot };
}

// Bailey looks people and services up by short refs (the last 8 characters
// of their ids), and the lookups carry the names. Gathered so a booking from
// a link can say who and what. A long service list is cut short when it's
// saved, so it's read with patterns, keeping as many as made it in.
const SERVICE = /"name":"((?:[^"\\]|\\.)*)"[^[{]*?"options":\[([^\]]*)/g;
const OPTION = /"stylist":"((?:[^"\\]|\\.)*)"[^}]*?"ref":"([^"]+)"[^}]*?"stylistRef":"([^"]+)"/g;
const unquote = (v) => { try { return JSON.parse(`"${v}"`); } catch (e) { return v; } };

function gatherNames(toolName, result, names) {
    const raw = typeof result === 'string' ? result : JSON.stringify(result || {});
    if (toolName === 'list_services') {
        SERVICE.lastIndex = 0;
        let m = SERVICE.exec(raw);
        while (m) {
            const name = unquote(m[1]);
            OPTION.lastIndex = 0;
            let o = OPTION.exec(m[2]);
            while (o) {
                names.services[o[2]] = name;
                names.stylists[o[3]] = unquote(o[1]);
                o = OPTION.exec(m[2]);
            }
            m = SERVICE.exec(raw);
        }
    }
    const r = toolName === 'check_availability' && parse(result);
    if (r && r.byStylist && typeof r.byStylist === 'object') {
        Object.keys(r.byStylist).forEach((who) => {
            const ref = r.byStylist[who] && r.byStylist[who].stylistRef;
            if (ref) names.stylists[ref] = who;
        });
    }
}

const refOf = (id) => String(id || '').slice(-8);

/**
 * One recorded step as something the owner can read, or null for lookups
 * the client never saw (prices, services, salon details).
 */
export function readStep(toolName, result, { phone = false } = {}) {
    const name = String(toolName || '');
    const r = parse(result) || {};
    if (name === 'check_availability') {
        const day = dayLabel(r.date || r.requestedDate);
        const slots = Array.isArray(r.slots) ? r.slots : [];
        if (slots.length) return { kind: 'times', icon: 'clock', text: `Found free times${day ? ` on ${day}` : ''}: ${slotsLine(slots)}`, key: `${r.date}|${slots.join(',')}` };
        const next = dayLabel(r.nextAvailableDate);
        return { kind: 'times', icon: 'clock', text: `No free times${day ? ` on ${day}` : ''}${next ? `. Next free day: ${next}` : ''}`, key: `${r.date}|none` };
    }
    if (name === 'propose_booking') {
        if (r.alreadyShown) return null;
        if (r.blockedByRule) {
            const consult = !!(r.rule && r.rule.requiresConsultation);
            const day = dayLabel(r.date);
            const booking = bookingFromLink(r.bookingLink);
            return {
                kind: 'blocked',
                icon: 'link',
                text: `${consult ? 'Needs a consultation first' : 'Can’t be booked here'}, so sent to your booking page`,
                offer: r.proposal || null,
                blocked: { date: r.date || null, slot: r.slot || null, consult, day },
                booking: booking && { ...booking, blocked: true, consult, rule: (r.rule && r.rule.consultationMessage) || '' }
            };
        }
        if (r.available === false) return { kind: 'times', icon: 'clock', text: 'That time had gone, so offered others' };
        if (r.proposal) return { kind: 'offer', icon: 'tag', text: `Offered ${describeOffer(r.proposal)}`, offer: r.proposal };
        return null;
    }
    if (name === 'create_booking') {
        if (r.booked) return { kind: 'booked', icon: 'tick', text: 'Booked it' };
        if (r.handoff === 'link' || r.bookingLink) return { kind: 'link', icon: 'link', text: phone ? 'Texted a booking link' : 'Sent a booking link', booking: bookingFromLink(r.bookingLink) };
        return null;
    }
    if (name === 'get_my_bookings') {
        if (r.refused && r.reason === 'unverified') return { kind: 'info', icon: 'info', text: 'Asked them to sign in to see their bookings' };
        return { kind: 'info', icon: 'info', text: 'Looked up their bookings' };
    }
    // Not seen on the server yet (docs/FRONT_DESK_BACKEND.md, ask 4): read
    // by name so they count as soon as they're recorded.
    if (/message/i.test(name) && r.ok !== false && r.saved !== false) return { kind: 'message', icon: 'chat', text: 'Took a message' };
    if (/transfer/i.test(name) && r.ok !== false) return { kind: 'transfer', icon: 'phone', text: r.to ? `Put the call through to ${r.to}` : 'Put the call through' };
    return null;
}

const GREETING = /^(?:oh,?\s*)?(?:hi|hiya|hey|hello|helo|morning|afternoon|evening|good (?:morning|afternoon|evening)|yo)(?:\s+there)?[\s!.,?]*$/i;
const NOTICE = /^(?:this|the) call (?:is being|may be) recorded[\s.!]*$/i;
// Signing off ("Thanks, bye!", "Cheers", "Perfect, thank you") says nothing about what they wanted
const GOODBYE = /^(?:(?:ok(?:ay)?|great|perfect|lovely|brilliant|amazing|cool|no worries|that'?s (?:great|perfect|lovely|all))[\s!.,]*)?(?:thanks?(?: you)?(?: so much| very much)?|thank you|cheers|ta|bye|goodbye|see you|speak soon)(?:[\s!.,]+(?:bye|goodbye|see you|x+))?[\s!.,x]*$/i;
export const isSmallTalk = (text) => {
    const t = String(text || '').trim();
    return GREETING.test(t) || NOTICE.test(t) || GOODBYE.test(t);
};

const OUTCOME_FROM_SERVER = { booked: 'booked', answered: 'answered', message: 'message', transferred: 'transfer', transfer: 'transfer', link: 'link' };

export const OUTCOMES = {
    booked: { label: 'Booked', tone: 'done' },
    link: { label: 'Link sent', tone: 'wait' },
    blocked: { label: 'Sent to booking page', tone: 'wait' },
    message: { label: 'Message', tone: 'wait' },
    transfer: { label: 'Put through', tone: 'plain' },
    answered: { label: 'Answered', tone: 'plain' }
};

/** A gap this long between lines puts a time in the transcript. */
const TIME_GAP = 3 * 60 * 1000;

export function readConversation(s) {
    const session = s || {};
    const phone = isPhoneChannel(session.channel);
    const raw = (session.messages || []).filter((m) => m && (m.text || m.content));
    const turns = [];
    let offer = null;
    let blocked = null;
    let lastEventKey = '';
    const seen = {};
    let lastTimed = null;
    let booking = null;
    const names = { stylists: {}, services: {} };

    raw.forEach((m) => {
        const text = String(m.text || m.content || '');
        const at = m.at ? new Date(m.at).getTime() : null;
        let turn = null;
        if (m.role === 'tool' || m.toolName) {
            gatherNames(m.toolName, text, names);
            const step = readStep(m.toolName, text, { phone });
            if (!step) return;
            if (step.offer) offer = step.offer;
            if (step.blocked) blocked = step.blocked;
            if (step.booking) booking = step.booking;
            seen[step.kind] = true;
            // The same free times found twice in a row say nothing new.
            if (step.key && step.key === lastEventKey) return;
            lastEventKey = step.key || '';
            turn = { kind: 'event', icon: step.icon, text: step.text, step: step.kind };
        } else if (m.role === 'user') {
            turn = { kind: 'client', text: text.trim() };
            lastEventKey = '';
        } else {
            turn = { kind: 'bailey', text: text.trim() };
            seen.reply = true;
            lastEventKey = '';
        }
        if (Number.isFinite(at) && turn.kind !== 'event' && (lastTimed == null || at - lastTimed >= TIME_GAP)) {
            turn.time = at;
            lastTimed = at;
        }
        turns.push(turn);
    });

    const server = OUTCOME_FROM_SERVER[String(session.outcome || '').toLowerCase()];
    const outcome = server
        || (seen.booked && 'booked')
        || (seen.link && 'link')
        || (seen.blocked && 'blocked')
        || (seen.message && 'message')
        || (seen.transfer && 'transfer')
        || (seen.reply && 'answered')
        || null;

    const firstAsk = turns.find((t) => t.kind === 'client' && !isSmallTalk(t.text));
    const titleRaw = session.summaryTitle
        || (offer && describeOffer(offer, { withTime: false, withPrice: false }))
        || (blocked && blocked.day && `Booking for ${blocked.day}`)
        || (firstAsk && firstAsk.text)
        || '';
    const title = titleRaw ? threadTitle({ title: titleRaw }) : (phone ? 'A call' : 'A chat');

    const clientName = String(session.clientName || '').trim();
    // Who and what, for a booking that came from a link.
    if (booking) {
        const same = offer && offer.date === booking.date && offer.slot === booking.slot;
        const services = booking.serviceIds.map((id) => names.services[refOf(id)]).filter(Boolean);
        booking = {
            ...booking,
            stylist: names.stylists[refOf(booking.staffId)] || (same && offer.stylist) || '',
            services: services.length === booking.serviceIds.length ? services : (same && Array.isArray(offer.services) ? offer.services : services)
        };
    }
    return {
        id: String(session._id || session.id || ''),
        channel: phone ? 'phone' : 'chat',
        known: !!session.identityVerified,
        clientName,
        clientPhone: String(session.clientPhone || '').trim(),
        at: session.lastActivityAt || session.createdAt || null,
        startedAt: session.createdAt || session.lastActivityAt || null,
        durationSec: phone ? Math.round(Number(session.voiceSeconds) || 0) : 0,
        title,
        outcome,
        offer,
        booking,
        summary: summaryFor(outcome, offer, phone, blocked),
        turns
    };
}

/** The one line at the top of a transcript, in Bailey's voice. Nothing for a plain answer. */
export function summaryFor(outcome, offer, phone, blocked = null) {
    const what = offer ? describeOffer(offer) : '';
    if (outcome === 'booked') return what ? `I booked ${lower(what)}.` : 'I booked them in.';
    if (outcome === 'link') {
        const verb = phone ? 'texted' : 'sent';
        return what ? `I ${verb} a booking link for ${lower(what)}, to confirm on your booking page.` : `I ${verb} a booking link, to confirm on your booking page.`;
    }
    if (outcome === 'blocked') {
        const when = blocked && blocked.day ? ` for ${blocked.day}${blocked.slot ? ` at ${timeLabel(blocked.slot)}` : ''}` : '';
        if (blocked && blocked.consult) return `The booking${when} needs a consultation first, so I sent them to your booking page to arrange it.`;
        return `The booking${when} can’t be made ${phone ? 'over the phone' : 'in chat'}, so I sent them to your booking page.`;
    }
    if (outcome === 'message') return 'I took a message. It’s in Messages.';
    if (outcome === 'transfer') return 'I put the call through.';
    return '';
}

// Service names are the salon's own, so they keep their capitals; only an
// article is added ("a Haircut", "an Eyebrow Wax").
function lower(what) {
    if (/^An appointment/.test(what)) return `an appointment${what.slice('An appointment'.length)}`;
    return `${/^[aeiou]/i.test(what) ? 'an' : 'a'} ${what}`;
}

/** "Call · 4:22 · a guest" for a row. */
export function rowMeta(c) {
    const who = c.clientName ? `${c.clientName}${c.known ? ', signed in' : ''}` : c.known ? 'signed in' : 'a guest';
    return [c.channel === 'phone' ? 'Call' : 'Chat', c.channel === 'phone' && clock(c.durationSec), who].filter(Boolean).join(' · ');
}

/** "Phone call · 4 min 22 s · a guest · Fri 11 Sept, 9:25am" for the top of a transcript. */
export function readerMeta(c, now = new Date()) {
    const who = c.clientName ? `${c.clientName}${c.known ? ', signed in' : ''}` : c.known ? 'a signed-in client' : 'a guest';
    const started = c.startedAt ? longWhen(c.startedAt, now) : '';
    return [c.channel === 'phone' ? 'Phone call' : 'Chat on your booking page', c.channel === 'phone' && length(c.durationSec), who, started].filter(Boolean).join(' · ');
}

/** "Today, 9:25am", "Yesterday, 8:12pm", "Fri 11 Sept, 9:25am". */
export function longWhen(value, now = new Date()) {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    const h = d.getHours();
    const t = `${(h % 12) || 12}:${String(d.getMinutes()).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`;
    return `${dayName(d, now)}, ${t}`;
}

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); }

function dayName(d, now) {
    const diff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    const sameYear = d.getFullYear() === now.getFullYear();
    return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}${sameYear ? '' : ` ${d.getFullYear()}`}`;
}

/** Day headings for the conversations page: Today, Yesterday, Monday, then dates. */
export function dayHeading(value, now = new Date()) {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return 'Earlier';
    const diff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff > 1 && diff < 7) return DAYS_LONG[d.getDay()];
    return dayName(d, now);
}

/** Newest first, grouped under day headings. */
export function groupByDay(conversations, now = new Date()) {
    const groups = [];
    // By when they started, as a call log reads.
    const start = (c) => new Date(c.startedAt || c.at || 0).getTime();
    const sorted = [...(conversations || [])].sort((a, b) => start(b) - start(a));
    sorted.forEach((c) => {
        const label = dayHeading(c.startedAt || c.at, now);
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(c);
        else groups.push({ label, items: [c] });
    });
    return groups;
}

/** Search a conversation by its title, who, and every word said. */
export function matches(c, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    const hay = [c.title, c.clientName, c.clientPhone, ...(c.turns || []).map((t) => t.text)].join(' \n ').toLowerCase();
    // A number typed with or without spaces still finds it.
    const digits = q.replace(/\D/g, '');
    if (digits.length >= 4 && hay.replace(/\D/g, '').includes(digits)) return true;
    return hay.includes(q);
}

/** "9:25am", for a row under a day heading. */
export function timeOfDay(value) {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    const h = d.getHours();
    return `${(h % 12) || 12}:${String(d.getMinutes()).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`;
}
